import React, { useEffect, useState } from "react";
import { 
  Table, 
  Modal, 
  Select, 
  message, 
  Button, 
  Tag, 
  Descriptions, 
  Input, 
  DatePicker, 
  Space,
  Card,
  Row,
  Col,
  Divider,
} from "antd";
import { 
  EyeOutlined, 
  EditOutlined, 
  SearchOutlined,
  FilterOutlined
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { apiHandle } from "../../config/apiHandle";
import { getAllStoresAsync } from "../../services/StoreManagementServices";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select

const QuotesManagement = () => {
  const { user } = useSelector((s) => s.userAuth);
  const dispatch = useDispatch();
  const { get_all_stores_data } = useSelector((s) => s.storeManagement);
  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [adminStatus, setAdminStatus] = useState("");
  const [vendorStatus, setVendorStatus] = useState("");
  const [assignedStore, setAssignedStore] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);

  // Search and Filter States
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [assignedLocationFilter, setAssignedLocationFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchQuotations();
    dispatch(getAllStoresAsync());
  }, []);

  useEffect(() => {
    applyFilters();
  }, [quotations, searchText, dateRange, statusFilter, assignedLocationFilter]);

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const response = await apiHandle.get("/quotations");
      setQuotations(response.data.data.orders);
    } catch (error) {
      message.error("Failed to fetch quotations");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...quotations];

    // Search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      result = result.filter(quote => 
        (quote.quotationNumber?.toLowerCase().includes(searchLower)) ||
        (quote.businessName?.toLowerCase().includes(searchLower)) ||
        (quote.email?.toLowerCase().includes(searchLower)) ||
        (quote.phone?.toLowerCase().includes(searchLower)) ||
        (quote.companyName?.toLowerCase().includes(searchLower))
      );
    }

    // Date range filter
    if (dateRange?.length === 2) {
      const [start, end] = dateRange;
      result = result.filter(quote => {
        const quoteDate = dayjs(quote.createdAt);
        return quoteDate.isAfter(start) && quoteDate.isBefore(end);
      });
    }

    // Status filter
    if (statusFilter) {
      result = result.filter(quote => 
        quote.adminStatus === statusFilter || 
        quote.vendorStatus === statusFilter
      );
    }

    // Location filter (only for Master Admin)
    if (assignedLocationFilter && user.role === "MasterAdmin") {
      result = result.filter(quote => 
        quote.assignedLocation?._id === assignedLocationFilter
      );
    }

    setFilteredQuotations(result);
  };

  const handleUpdateStatus = async () => {
    if (!selectedQuote) {
      return message.warning("No quotation selected");
    }

    const payload = {};
    if (user.role === "Admin") {
      payload.adminStatus = adminStatus;
      if (assignedStore) {
        payload.assignedLocation = assignedStore;
        payload.adminStatus = "Assigned";
        payload.vendorStatus = "Pending";
      }
    } else if (user.role === "Vendor") {
      payload.vendorStatus = vendorStatus;
    }

    try {
      await apiHandle.patch(`/quotations/${selectedQuote._id}`, payload);
      message.success("Status updated successfully");
      fetchQuotations();
      setIsModalVisible(false);
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  const openViewModal = (record) => {
    setSelectedQuote(record);
    setIsViewModalVisible(true);
  };

  const openUpdateModal = (record) => {
    setSelectedQuote(record);
    setAdminStatus(record.adminStatus);
    setVendorStatus(record.vendorStatus);
    setAssignedStore(record.assignedLocation?._id || null);
    setIsModalVisible(true);
  };

  const handleStoreChange = (value) => {
    setAssignedStore(value);
    if (user.role === "Admin") {
      setAdminStatus("Assigned");
      setVendorStatus("Pending");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved": return "green";
      case "Rejected": return "red";
      case "Pending": return "orange";
      case "Assigned": return "blue";
      case "Awaiting Review": return "gold";
      case "Quoted": return "cyan";
      default: return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const renderDescriptionItem = (label, value) => {
    if (!value && value !== 0) return null;

    if (label === "Admin Status" || label === "Vendor Status") {
      return (
        <Descriptions.Item label={label}>
          <Tag color={getStatusColor(value)}>{value}</Tag>
        </Descriptions.Item>
      );
    } else if (label === "Attached File" && value) {
      return (
        <Descriptions.Item label={label}>
          <a href={value} target="_blank" rel="noopener noreferrer">
            View File
          </a>
        </Descriptions.Item>
      );
    } else if (label.includes("Date")) {
      return <Descriptions.Item label={label}>{formatDate(value)}</Descriptions.Item>;
    } else {
      return <Descriptions.Item label={label}>{value}</Descriptions.Item>;
    }
  };

  const columns = [
    {
      title: "Quotation #",
      dataIndex: "quotationNumber",
      key: "quotationNumber",
    },
    {
      title: "Business Name",
      dataIndex: "businessName",
      key: "businessName",
      render: (text) => text || "N/A",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Admin Status",
      dataIndex: "adminStatus",
      key: "adminStatus",
      render: (text) => <Tag color={getStatusColor(text)}>{text}</Tag>,
    },
    {
      title: "Vendor Status",
      dataIndex: "vendorStatus",
      key: "vendorStatus",
      render: (text) => <Tag color={getStatusColor(text)}>{text}</Tag>,
    },
    {
      title: "Assigned Store",
      dataIndex: "assignedLocation",
      key: "assignedLocation",
      render: (store) => store?.name || "Not Assigned",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => openViewModal(record)}>
            View
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => openUpdateModal(record)}
          >
            Update
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <h1 className="text-3xl font-bold text-center mb-6">Custom Quotations</h1>
      
      {/* Search and Filter Section */}
      <Card className="mb-6">
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search quotations..."
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Col>
        </Row>

        {showFilters && (
          <div className="mt-4">
            <Divider orientation="left">Filters</Divider>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <RangePicker 
                  style={{ width: '100%' }} 
                  onChange={(dates) => setDateRange(dates)} 
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Status"
                  onChange={(value) => setStatusFilter(value)}
                  allowClear
                >
                  <Select.Option value="Awaiting Review">Awaiting Review</Select.Option>
                  <Select.Option value="Assigned">Assigned</Select.Option>
                  <Select.Option value="Pending">Pending</Select.Option>
                  <Select.Option value="Quoted">Quoted</Select.Option>
                  <Select.Option value="Approved">Approved</Select.Option>
                  <Select.Option value="Rejected">Rejected</Select.Option>
                </Select>
              </Col>
              {user.role === "MasterAdmin" && (
                <Col xs={24} sm={12} md={8}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Assigned Location"
                    onChange={(value) => setAssignedLocationFilter(value)}
                    allowClear
                  >
                    {get_all_stores_data?.stores?.map(store => (
                      <Select.Option key={store._id} value={store._id}>
                        {store.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              )}
            </Row>
          </div>
        )}
      </Card>

      {/* Quotations Table */}
      <Table
        dataSource={filteredQuotations.length ? filteredQuotations : quotations}
        columns={columns}
        loading={loading}
        rowKey="_id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"]
        }}
      />

      {/* View Details Modal */}
      <Modal
        title="Quotation Details"
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedQuote && (
          <Descriptions bordered column={1}>
            {renderDescriptionItem("Quotation #", selectedQuote.quotationNumber)}
            {renderDescriptionItem("Business Name", selectedQuote.businessName)}
            {renderDescriptionItem("First Name", selectedQuote.firstName)}
            {renderDescriptionItem("Last Name", selectedQuote.lastName)}
            {renderDescriptionItem("Email", selectedQuote.email)}
            {renderDescriptionItem("Phone", selectedQuote.phone)}
            {renderDescriptionItem("Company Name", selectedQuote.companyName)}
            {renderDescriptionItem("Country", selectedQuote.country)}
            {renderDescriptionItem("Address", selectedQuote.address)}
            {renderDescriptionItem("City", selectedQuote.city)}
            {renderDescriptionItem("Province", selectedQuote.province)}
            {renderDescriptionItem("Postal Code", selectedQuote.postalCode)}
            {renderDescriptionItem("Industry Segment", selectedQuote.industry_segment)}
            {renderDescriptionItem("Project", selectedQuote.project)}
            {renderDescriptionItem("Delivery Date", selectedQuote.desired_delivery_date)}
            {renderDescriptionItem("Admin Status", selectedQuote.adminStatus)}
            {renderDescriptionItem("Vendor Status", selectedQuote.vendorStatus)}
            {renderDescriptionItem("Assigned Location", selectedQuote.assignedLocation?.name)}
            {renderDescriptionItem("Attached File", selectedQuote.file)}
            {renderDescriptionItem("Created At", selectedQuote.createdAt)}
            {renderDescriptionItem("Updated At", selectedQuote.updatedAt)}

            {selectedQuote.items && (
              <Descriptions.Item label="Order Items">
                <Descriptions bordered column={1} size="small">
                  {Object.entries(selectedQuote.items).map(([key, value]) => (
                    value && (
                      <Descriptions.Item label={key.replace(/_/g, ' ')} key={key}>
                        {value}
                      </Descriptions.Item>
                    )
                  ))}
                </Descriptions>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Update Status Modal */}
      <Modal
        title="Update Quotation Status"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleUpdateStatus}
        okText="Update"
      >
        {user.role === "Admin" && (
          <>
            <div className="mb-4">
              <label className="block mb-2">Admin Status</label>
              <Select
                style={{ width: "100%" }}
                value={adminStatus}
                onChange={setAdminStatus}
                options={[
                  { label: "Awaiting Review", value: "Awaiting Review" },
                  { label: "Assigned", value: "Assigned" },
                ]}
              />
            </div>
            <div>
              <label className="block mb-2">Assign to Store</label>
              <Select
                style={{ width: "100%" }}
                value={assignedStore}
                onChange={handleStoreChange}
                placeholder="Select store"
              >
                {get_all_stores_data?.stores?.map(store => (
                  <Option key={store._id} value={store._id}>
                    {store.name}
                  </Option>
                ))}
              </Select>
            </div>
          </>
        )}
        {user.role === "Vendor" && (
          <div>
            <label className="block mb-2">Vendor Status</label>
            <Select
              style={{ width: "100%" }}
              value={vendorStatus}
              onChange={setVendorStatus}
              options={[
                { label: "Pending", value: "Pending" },
                { label: "Quoted", value: "Quoted" },
                { label: "Approved", value: "Approved" },
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default QuotesManagement;
